import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  AbilityEffect,
  AttachedTable,
  ConsumedResource,
  SpellOverrides,
  UpgradableNumber,
} from "@dndModels";
import { 
  ABILITY_SCORES_EXPANDED,
  // CASTING_TIMES,
  COMPONENTS,
  DICE_SIZE_OPTIONS,
  DURATIONS,
  LEVELED_SPELL_LEVELS_STRINGS,
  // REFRESH_RULES,
  STANDARD_CASTING_TIMES,
} from "@dndModels/Constants";
import { 
  AbilityScoreExpandedType, 
  CastingTime, 
  DurationType,
  SpellLevel, 
} from "@dndModels/Types";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";

import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
// import SelectResourceBox from '../select/SelectResourceBox';
import CheckBox from "@components/input/CheckBox";
import ConsumedResourceInput from "@dndComponents/feature/ConsumedResourceInput";
import EffectsInput from "@dndComponents/feature/EffectsInput";
import { ModelBase } from "@models";

interface Props {
  obj: SpellOverrides;
  for_obj?: ModelBase; 
  disabled: boolean;
  attached_table?: AttachedTable;
  onChange: (changed: SpellOverrides) => void;
}
export default function SpellOverridesInput({
  obj,
  for_obj,
  disabled,
  attached_table,
  onChange,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 6
        }}>
        <SelectObjectBox
          name="Count as Being From Class"
          data_type="game_class"
          extra_options={["Don't Override"]}
          value={obj.count_as_class_id} 
          disabled={disabled}
          auto_sort
          dominant_only
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.count_as_class_id = changed;
            new_obj.spellcasting_ability = "None";
            onChange(new_obj);
            setPing(!ping);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: obj.spellcasting_ability === "Selectable" ? 3 : 6
        }}>
        { obj.count_as_class_id === "Don't Override" &&
          <SelectStringBox 
            name="Spellcasting Ability"
            options={ABILITY_SCORES_EXPANDED}
            extra_options={["Don't Override"]}
            value={obj.spellcasting_ability}
            disabled={disabled}
            onChange={(changed: AbilityScoreExpandedType | "Don't Override") => {
              const new_obj = obj;
              new_obj.spellcasting_ability = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          /> 
        }
      </Grid>
      { obj.spellcasting_ability === "Selectable" && 
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox
            name="Selectable Key"
            value={ obj.spellcasting_ability_selectable_key } 
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.spellcasting_ability_selectable_key = changed;
              onChange(new_obj);
              setPing(!ping);
            }} 
          />
        </Grid>
      }
      <Grid
        size={{
          xs: obj.override_dc_atk_mod ? 3 : 12
        }}>
        <CheckBox 
          name="Override Atk, DC, and Mod"
          disabled={disabled}
          value={ obj.override_dc_atk_mod }
          onChange={(changed: boolean) => {
            const new_obj = obj;
            new_obj.override_dc_atk_mod = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      { obj.override_dc_atk_mod &&
        <>
          <Grid
            size={{
              xs: 3
            }}>
            <UpgradableNumberBox 
              name="Spell Attack"
              disabled={disabled}
              value={ obj.spell_attack }
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.spell_attack = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <UpgradableNumberBox 
              name="Spell DC"
              disabled={disabled}
              value={ obj.spell_dc }
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.spell_dc = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <UpgradableNumberBox 
              name="Spell Mod"
              disabled={disabled}
              value={ obj.spell_mod }
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.spell_mod = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </>
      }
      <Grid
        size={{
          xs: 3
        }}>
        <SelectStringBox 
          name="Ritual Override"
          disabled={disabled}
          options={["Don't Override","Ritual","No Ritual"]}
          value={ obj.ritual_override }
          onChange={(changed: "Don't Override" | "Ritual" | "No Ritual") => {
            const new_obj = obj;
            new_obj.ritual_override = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 3
        }}>
        <SelectStringBox 
          name="Concentration Override"
          disabled={disabled}
          options={["Don't Override","Concentration","No Concentration"]}
          value={ obj.concentration_override }
          onChange={(changed: "Don't Override" | "Concentration" | "No Concentration") => {
            const new_obj = obj;
            new_obj.concentration_override = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 3
        }}>
        <SelectStringBox 
          name="Duration Override"
          disabled={disabled}
          options={["Don't Override",...DURATIONS]}
          value={ obj.duration_override }
          onChange={(changed: DurationType | "Don't Override") => {
            const new_obj = obj;
            new_obj.duration_override = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 3
        }}>
        <SelectStringBox 
          name="Components Override"
          options={[...COMPONENTS,"Don't Override","No V","No S","No M","No Consume"]}
          values={obj.components_override}
          multiple
          disabled={disabled}
          onChange={(changed: ("Don't Override" | "No V" | "No S" | "No M" | "No Consume" | "V" | "S" | "M")[]) => {
            const new_obj = obj;
            new_obj.components_override = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      { obj.components_override.includes("M") &&
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            name="Material Component"
            disabled={disabled}
            value={obj.material_component_override}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.material_component_override = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          /> 
        </Grid>
      }
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Range Override"
          disabled={disabled}
          options={["Don't Override","Replace","Add","Multiply"]}
          value={obj.range_override}
          onChange={(changed: "Don't Override" | "Replace" | "Add" | "Multiply") => {
            const new_obj = obj;
            new_obj.range_override = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        { obj.range_override !== "Don't Override" && 
          <UpgradableNumberBox 
            name="Range Override"
            disabled={disabled}
            value={ obj.range_override_num }
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.range_override_num = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        }
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Reaction Override"
          disabled={disabled}
          options={["Don't Override","Reaction","No Reaction"]}
          value={ obj.reaction_override }
          onChange={(changed: "Don't Override" | "Reaction" | "No Reaction") => {
            const new_obj = obj;
            new_obj.reaction_override = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <CheckBox 
          name="Maximize Dice Roll"
          disabled={disabled}
          value={ obj.maximize_dice_roll }
          onChange={(changed: boolean) => {
            const new_obj = obj;
            new_obj.maximize_dice_roll = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <StringBox
          name="Minimum Damage Die Rolls"
          value={`${obj.minimum_damage_die_roll}`}
          type="number"
          disabled={disabled}
          onBlur={(changed: number) => {
            const new_obj = obj;
            new_obj.minimum_damage_die_roll = +changed;
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <StringBox
          name="Maximum Dice Minned"
          value={`${obj.max_dice_minned}`}
          type="number"
          disabled={disabled}
          onBlur={(changed: number) => {
            const new_obj = obj;
            new_obj.max_dice_minned = +changed;
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name="Damage Dice Size Override"
          value={ obj.damage_die_size_override === -1 ? "Don't Override" : `${obj.damage_die_size_override}` }
          options={DICE_SIZE_OPTIONS}
          extra_options={["Don't Override"]}
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            if (changed === "Don't Override") {
              new_obj.damage_die_size_override = -1;
            } else {
              new_obj.damage_die_size_override = +changed;
            }
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <StringBox
          name="Damage Die Reroll Threshold"
          value={`${obj.damage_die_reroll_threshold}`}
          type="number"
          disabled={disabled}
          onBlur={(changed: number) => {
            const new_obj = obj;
            new_obj.damage_die_reroll_threshold = +changed;
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <StringBox
          name="Maximum Dice Rerolled"
          value={`${obj.max_dice_rerolled}`}
          type="number"
          disabled={disabled}
          onBlur={(changed: number) => {
            const new_obj = obj;
            new_obj.max_dice_rerolled = +changed;
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox 
          name="Include Spellcasting Modifier on Damage"
          disabled={disabled}
          options={["Don't Override","True","False"]}
          value={obj.include_modifier_on_damage}
          onChange={(changed: "Don't Override" | "True" | "False") => {
            const new_obj = obj;
            new_obj.include_modifier_on_damage = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 3
        }}>
        <SelectStringBox 
          name="Casting Time Override"
          disabled={disabled}
          options={STANDARD_CASTING_TIMES}
          extra_options={["Don't Override","Options"]}
          value={obj.casting_time_override}
          onChange={(changed: "Don't Override" | "Options" | CastingTime) => {
            const new_obj = obj;
            new_obj.casting_time_override = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 3
        }}>
        { obj.casting_time_override === "Options" && 
          <SelectStringBox 
            name="Casting Time Options"
            disabled={disabled}
            options={STANDARD_CASTING_TIMES}
            extra_options={["Don't Override"]}
            values={obj.casting_time_options}
            onChange={(changed: ("Don't Override" | CastingTime)[]) => {
              const new_obj = obj;
              new_obj.casting_time_options = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          /> 
        }
      </Grid>
      <Grid
        size={{
          xs: 3
        }}>
        <SelectStringBox 
          name="Slot Override"
          disabled={disabled}
          options={["Don't Override","At Will","At Will or Slot Upcast","Only Special Resource","Or Special Resource","And Special Resource"]}
          value={obj.slot_override}
          onChange={(changed: "Don't Override" | "At Will" | "At Will or Slot Upcast" | "Only Special Resource" | "Or Special Resource" | "And Special Resource") => {
            const new_obj = obj;
            new_obj.slot_override = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      <Grid
        size={{
          xs: 3
        }}>
        <SelectStringBox 
          name="Cast At Level"
          disabled={disabled}
          options={LEVELED_SPELL_LEVELS_STRINGS}
          extra_options={["Don't Override"]}
          value={`${obj.cast_at_level}`}
          onChange={(changed: "Don't Override" | SpellLevel) => {
            const new_obj = obj;
            new_obj.cast_at_level = changed === "Don't Override" ? changed : (+changed as SpellLevel);
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      { !["Don't Override","At Will","At Will or Slot Upcast"].includes(obj.slot_override) &&
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <ConsumedResourceInput 
              obj={obj.consumed_resource} 
              disabled={false}
              onChange={(changed: ConsumedResource) => {
                const new_obj = obj;
                new_obj.consumed_resource = changed;
                onChange(new_obj);
                setReloading(true);
              }}
            />
          </Grid>
        </>
      }
      { obj.slot_override !== "At Will" && obj.slot_override !== "Only Special Resource" && obj.slot_override !== "Don't Override" && obj.slot_override !== "And Special Resource" &&
        <Grid
          size={{
            xs: 12
          }}>
          <CheckBox 
            name="Apply Overrides on Slot Casting"
            disabled={disabled}
            value={ obj.apply_overrides_on_slot_casting }
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.apply_overrides_on_slot_casting = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          /> 
        </Grid>
      }
      <Grid
        className='header'
        size={{
          xs: 12
        }}>Extra Effects</Grid>
      <Grid
        size={{
          xs: obj.potence_type === "Class" ? 6 : 12
        }}>
        <SelectStringBox 
          name="Potence Type"
          options={["Slot","Character","Class","None"]}
          value={obj.potence_type} 
          disabled={disabled}
          onChange={(changed: string) => {
            const floating_feature = obj;
            floating_feature.potence_type = changed;
            onChange(floating_feature);
            setPing(!ping);
          }}
        />
      </Grid>
      { obj.potence_type === "Class" && 
        <Grid
          size={{
            xs: 6
          }}>
          <SelectObjectBox
            name="Potence Class"
            data_type="game_class"
            value={obj.potence_class}
            disabled={disabled}
            onChange={(changed: string) => {
              const floating_feature = obj;
              floating_feature.potence_class = changed;
              onChange(floating_feature);
              setPing(!ping);
            }}
          />
        </Grid>
      }
      <Grid
        size={{
          xs: 6
        }}>
        <UpgradableNumberBox 
          name="Attack Bonus"
          value={obj.attack_bonus} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const floating_feature = obj;
            floating_feature.attack_bonus = changed;
            onChange(floating_feature);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <UpgradableNumberBox 
          name="Save DC"
          value={obj.save_dc} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const floating_feature = obj;
            floating_feature.save_dc = changed;
            onChange(floating_feature);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <EffectsInput
          effects={obj.effects}
          disabled={disabled}
          for_obj={for_obj}
          onChange={(changed: AbilityEffect[]) => {
            const new_obj = obj;
            new_obj.effects = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    </Grid>
  );
}

